import { useState } from 'react';
import type { DocumentMarkupTool } from '../../types';

/** Pen / line / callout sizes. Thin matches the previous hardcoded 1.1 default. */
export const MARKUP_PEN_WIDTHS = [1.1, 2.4, 4.5] as const;

/** Highlighter sizes. Thin matches the previous 1.1 × 4.5 default. */
export const MARKUP_HIGHLIGHTER_WIDTHS = [5, 10, 18] as const;

export const DEFAULT_MARKUP_PEN_WIDTH = MARKUP_PEN_WIDTHS[0];
export const DEFAULT_MARKUP_HIGHLIGHTER_WIDTH = MARKUP_HIGHLIGHTER_WIDTHS[0];

export function markupWidthsForTool(tool: DocumentMarkupTool): readonly number[] {
  return tool === 'highlighter' ? MARKUP_HIGHLIGHTER_WIDTHS : MARKUP_PEN_WIDTHS;
}

export function defaultMarkupWidthForTool(tool: DocumentMarkupTool): number {
  return tool === 'highlighter' ? DEFAULT_MARKUP_HIGHLIGHTER_WIDTH : DEFAULT_MARKUP_PEN_WIDTH;
}

export function markupWidthChipPx(width: number, tool: DocumentMarkupTool): number {
  const set = markupWidthsForTool(tool);
  const idx = Math.max(0, set.findIndex((w) => w === width));
  return tool === 'highlighter' ? 8 + idx * 4 : 6 + idx * 4;
}

export function markupWidthLabel(width: number, tool: DocumentMarkupTool): string {
  const set = markupWidthsForTool(tool);
  const idx = set.findIndex((w) => w === width);
  if (idx === 0) return 'Thin';
  if (idx === set.length - 1) return 'Thick';
  return 'Medium';
}

export function showsMarkupStrokeWidth(tool: DocumentMarkupTool): boolean {
  return tool === 'pen' || tool === 'highlighter' || tool === 'line' || tool === 'callout' || tool === 'cloud';
}

export function useMarkupStrokeWidth(tool: DocumentMarkupTool) {
  const [penWidth, setPenWidth] = useState<number>(DEFAULT_MARKUP_PEN_WIDTH);
  const [highlighterWidth, setHighlighterWidth] = useState<number>(DEFAULT_MARKUP_HIGHLIGHTER_WIDTH);
  const strokeWidth = tool === 'highlighter' ? highlighterWidth : penWidth;
  const setStrokeWidth = (width: number) => {
    if (tool === 'highlighter') setHighlighterWidth(width);
    else setPenWidth(width);
  };
  return {
    strokeWidth,
    setStrokeWidth,
    widths: markupWidthsForTool(tool),
  };
}

/** Persist the chosen size with a small zoom-aware bump for pen/line. */
export function resolveMarkupStrokeWidth(
  tool: DocumentMarkupTool,
  baseStrokeWidth: number,
  scale: number,
): number {
  const base = Number.isFinite(baseStrokeWidth) ? baseStrokeWidth : defaultMarkupWidthForTool(tool);
  if (tool === 'highlighter') return Math.max(2, base);
  if (tool === 'callout' || tool === 'stamp' || tool === 'cloud' || tool === 'typewriter') return Math.max(1.1, base);
  return Math.max(1.1, base * (1 + Math.min(0.5, (scale - 0.5) * 0.15)));
}
